import { IconButton } from "@components/button";
import ErrorDetail from "@components/errorDetail/ErrorDetail";
import { CloseIcon } from "@components/icon";
import { isCurrentDay } from "@components/inputs/date/utils";
import Tooltip from "@components/tooltip";
import dayjs from "dayjs";
import React from "react";
import { WithTranslation, withTranslation } from "react-i18next";

import { AvatarSize, IconSize, MouseButton } from "../enums";
import i18n from "../i18n";
import TestIds from "../testIds";
import DateType from "../types/Date";
import { INotification } from "./Notifications";
import {
    DescriptionWrapper,
    NotificationAvatar,
    NotificationContentWrapper,
    NotificationDescription,
    NotificationInfo,
    NotificationSubtitle,
    NotificationTime,
    NotificationTitle,
    StyledNotification,
    TopRightWrapper
} from "./Notifications.styles";

interface IProps extends INotification {
    onClick?: () => void;
    onCloseButtonClick?: () => void;
}

class Notification extends React.PureComponent<IProps & WithTranslation> {
    handleNotificationClick = (event: React.SyntheticEvent): void => {
        if ((event as React.MouseEvent).button === MouseButton.MainButton) {
            this.props.onClick?.();
        }
    };

    getDateTime = (): string => {
        let dateTimeString: string;

        if (isCurrentDay(dayjs(this.props.time))) {
            dateTimeString = `${this.props.t("Components:Notifications.Today")}, ${DateType.localFormat(this.props.time, DateType.defaultTimeFormat)}`;
        } else if (dayjs(this.props.time).isSame(dayjs().subtract(1, "day"), "day")) {
            dateTimeString = `${this.props.t("Components:Notifications.Yesterday")}, ${DateType.localFormat(this.props.time, DateType.defaultTimeFormat)}`;
        } else {
            dateTimeString = DateType.localFormat(this.props.time, DateType.defaultDateFormat);
        }

        return dateTimeString;
    };

    render() {
        return (
            <StyledNotification $isUnread={this.props.isUnread}
                                onClick={this.props.onClick ? this.handleNotificationClick : null}
                                tabIndex={0}
                                isWithoutStyle
                                link={this.props.url}>
                <NotificationAvatar src={this.props.avatarSrc}
                                    size={AvatarSize.SM}
                                    isRound>
                </NotificationAvatar>
                <NotificationContentWrapper>
                    <NotificationTitle data-testid={TestIds.Title}>
                        {this.props.title}
                    </NotificationTitle>
                    <NotificationSubtitle data-testid={TestIds.Subtitle}>
                        {this.props.subtitle}
                    </NotificationSubtitle>
                    <Tooltip content={this.props.info} onlyShowWhenChildrenOverflowing>
                        {(ref) => (
                            <NotificationInfo data-testid={TestIds.NotificationInfo}
                                              ref={ref}>
                                {this.props.info}
                            </NotificationInfo>
                        )}
                    </Tooltip>
                    <Tooltip content={this.props.description}
                             onlyShowWhenChildrenOverflowing>
                        {(ref) => (
                            <DescriptionWrapper>
                                <NotificationDescription data-testid={TestIds.NotificationDescription}
                                                         ref={ref}>
                                    {this.props.description}
                                </NotificationDescription>
                                {this.props.detailData &&
                                    <ErrorDetail data={this.props.detailData}
                                                 size={IconSize.S}
                                                 isLight
                                                 style={{ position: "relative", top: "1px" }}
                                    />
                                }
                            </DescriptionWrapper>
                        )}
                    </Tooltip>
                </NotificationContentWrapper>
                <TopRightWrapper>
                    {this.props.onCloseButtonClick ? (
                        <IconButton onClick={this.props.onCloseButtonClick}
                                    isLight
                                    title={i18n.t("Common:General.Close")}
                                    isDecorative>
                            <CloseIcon width={IconSize.M} height={IconSize.M}/>
                        </IconButton>
                    ) : (
                        <NotificationTime>
                            {this.getDateTime()}
                        </NotificationTime>
                    )}
                </TopRightWrapper>
            </StyledNotification>
        );
    }
}

export default withTranslation(["Components"])(Notification);